import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import API from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
  ArrowDownLeft, ArrowUpRight, ChevronDown, RefreshCw, CircleCheck,
  CircleX, CircleAlert, CircleDashed,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const STATUS_META = {
  delivered: { Icon: CircleCheck, label: "Delivered", color: "text-green-600 bg-green-50 border-green-200" },
  sent:      { Icon: CircleCheck, label: "Sent",      color: "text-green-600 bg-green-50 border-green-200" },
  skipped:   { Icon: CircleDashed, label: "Skipped",  color: "text-muted-foreground bg-muted border-border" },
  not_found: { Icon: CircleAlert, label: "No match",  color: "text-amber-700 bg-amber-50 border-amber-200" },
  failed:    { Icon: CircleX,     label: "Failed",    color: "text-red-700 bg-red-50 border-red-200" },
  error:     { Icon: CircleX,     label: "Error",     color: "text-red-700 bg-red-50 border-red-200" },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.error;
  const { Icon, label, color } = meta;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${color}`}>
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
}

function DirectionBadge({ direction }) {
  const isIn = direction === "inbound";
  const Icon = isIn ? ArrowDownLeft : ArrowUpRight;
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground">
      <Icon className="w-3 h-3" /> {isIn ? "Spruce → Portal" : "Portal → Spruce"}
    </span>
  );
}

function LogRow({ row }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="spruce-log-row">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left px-4 py-3 hover:bg-accent/50 transition-colors flex items-start gap-3"
        data-testid="spruce-log-row-toggle"
      >
        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <DirectionBadge direction={row.direction} />
            <StatusBadge status={row.status} />
            {row.sender_role && (
              <span className="text-[11px] text-muted-foreground">· {row.sender_role}</span>
            )}
            {row.http_code && (
              <span className="text-[11px] text-muted-foreground">· HTTP {row.http_code}</span>
            )}
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium truncate">
              {row.resolved_patient_email || "(no patient matched)"}
            </span>
            {row.conversation_id && (
              <span className="text-[10px] font-mono text-muted-foreground truncate">
                convo: {row.conversation_id.slice(-8)}
              </span>
            )}
          </div>
          {row.message_preview && (
            <p className="text-xs text-muted-foreground truncate">"{row.message_preview}"</p>
          )}
          {row.reason && (
            <p className="text-xs text-amber-700">{row.reason}</p>
          )}
        </div>
        <div className="text-right shrink-0">
          <p className="text-[11px] text-muted-foreground">
            {row.created_date ? formatDistanceToNow(new Date(row.created_date), { addSuffix: true }) : ""}
          </p>
          <ChevronDown className={`w-4 h-4 text-muted-foreground mt-1 ml-auto transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && (
        <div className="border-t border-border bg-muted/30 px-4 py-3" data-testid="spruce-log-row-raw">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Raw payload</p>
          <pre className="text-[11px] leading-relaxed font-mono whitespace-pre-wrap break-words max-h-96 overflow-y-auto text-foreground/80">
            {JSON.stringify(row.raw_payload, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

export default function SpruceLogs() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | inbound | outbound | failures

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/admin/spruce-logs", { params: { limit: 200 } });
      setRows(data.logs || []);
      setTotal(data.total || 0);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!user) return;
    if (user.role !== "admin") { navigate("/"); return; }
    load();
  }, [user, navigate]);

  const visible = rows.filter((r) => {
    if (filter === "inbound") return r.direction === "inbound";
    if (filter === "outbound") return r.direction === "outbound";
    if (filter === "failures") return ["failed", "error", "not_found"].includes(r.status);
    return true;
  });

  const counts = {
    all: rows.length,
    inbound: rows.filter((r) => r.direction === "inbound").length,
    outbound: rows.filter((r) => r.direction === "outbound").length,
    failures: rows.filter((r) => ["failed", "error", "not_found"].includes(r.status)).length,
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-4 pb-24 space-y-4" data-testid="spruce-logs-page">
      <PageHeader title="Spruce Webhook Logs" subtitle={`Last ${rows.length} of ${total} events`} />
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 flex-wrap">
          {["all", "inbound", "outbound", "failures"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                filter === f ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-foreground hover:bg-accent"
              }`}
              data-testid={`spruce-logs-filter-${f}`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)} ({counts[f]})
            </button>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} className="rounded-full gap-1.5" data-testid="spruce-logs-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground text-sm">Loading…</div>
      ) : visible.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm" data-testid="spruce-logs-empty">
          No webhook events {filter !== "all" ? `match "${filter}"` : "yet"}.
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map((r, i) => (
            <LogRow key={`${r.created_date}-${i}`} row={r} />
          ))}
        </div>
      )}
    </div>
  );
}
